import { State } from '@/app/state/model';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Platform } from '@egovsolutions/angular-platform';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { selectPPTAccount } from '../../../state/entities/account/ppt-account.selectors';
import { selectPPTBlockMessages } from '../../../state/ppt-state/ppt-state.selectors';

@Component({
    selector: 'ppt-account-balance',
    template: `
        <ng-container *ngIf="due$ | async as due; else placeholder">
            <!-- Plain -->

            <ng-template #totalDue let-favorNa="favorNa">
                <ng-container *ngIf="due.total">
                    {{ due.total | currency }}
                    <ng-container *ngIf="showDueWord">due</ng-container>
                    <br *ngIf="multiline" />
                    <ng-container *ngIf="showYears">
                        {{ due.years }}
                        <ng-container [ngPlural]="due.years">
                            <ng-template ngPluralCase="one">year</ng-template>
                            <ng-template ngPluralCase="other">years</ng-template>
                        </ng-container>
                    </ng-container>
                </ng-container>
                <span *ngIf="!due.total" class="unimportant-text">{{ favorNa ? "N / A" : noBalanceLabel }}</span>
            </ng-template>

            <!-- Annotated -->

            <ng-template #annotatedAmount>
                <span
                    class="annotated-value"
                    [egovTooltip]="incompleteDueTooltip"
                    [style.cursor]="'default'"
                    (click)="$event.stopPropagation(); $event.preventDefault()"
                >
                    <mat-icon>info</mat-icon>
                    <ng-container *ngTemplateOutlet="totalDue; context: { favorNa: true }"></ng-container>
                </span>
            </ng-template>

            <!-- Output one or the other -->

            <ng-container
                *ngTemplateOutlet="
                    !(account$ | async)!.balanceViewBlockedForSomeBills && !(account$ | async)!.paymentBlockedForSomeBills && !(account$ | async)!.unpaidBillViewBlocked
                        ? totalDue
                        : annotatedAmount
                "
            ></ng-container>
        </ng-container>

        <!-- Placeholder -->

        <ng-template #placeholder>
            <egov-text-ui-placeholder length="7"></egov-text-ui-placeholder>
        </ng-template>

        <!-- Templates -->

        <ng-template #incompleteDueTooltip>
            <div style="max-width: 350px" [innerHTML]="(blockMessages$ | async)?.notAllBillsIncludedInAmountDue | MarkdownToHtml"></div>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PPTAccountBalanceComponent
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,


        // Template dependencies

        protected readonly platform: Platform
    )
    {}


    // Configuration

    @Input() protected set accountNumber(aN: string | undefined) { this.accountNumber$.next(aN); }
    private readonly accountNumber$ = new BehaviorSubject<string | undefined>(undefined);

    @Input() protected showYears = false;
    @Input() protected multiline = true;
    @Input() protected noBalanceLabel = 'All Paid';
    @Input() protected showDueWord = true;


    // Account data

    protected readonly account$ = this.accountNumber$.pipe(
        switchMap(aN => this.store.select(selectPPTAccount(aN)))
    );

    protected readonly due$ = this.account$.pipe(map(a => a?.due));


    // Block messages

    protected readonly blockMessages$ = this.store.select(selectPPTBlockMessages);
}
